// How far a live widget's loading has got (Ben, 2026-10-16: "make the holding page sexier").
// query.ts reports each call a widget starts and finishes here; the loading screen
// (LoadState.tsx) reads it to show "step 2 of 3" and what's happening now. The server doesn't
// say how big an answer is or how long it'll take, so steps are the honest measure.

import { useSyncExternalStore } from "react";

export interface LoadProgress {
  /** Calls that have finished. */
  done: number;
  /** The latest call still on its way (its query key), or null. */
  current: string | null;
}

const EMPTY: LoadProgress = { done: 0, current: null };
const progress = new WeakMap<HTMLElement, LoadProgress>();
const pending = new WeakMap<HTMLElement, Set<string>>();
const listeners = new Set<() => void>();
// Deferred: calls start while a widget is rendering, and updating the loading screen in the same
// moment is a React error ("Cannot update a component while rendering a different component").
let emitQueued = false;
const emit = () => {
  if (emitQueued) return;
  emitQueued = true;
  queueMicrotask(() => {
    emitQueued = false;
    listeners.forEach((l) => l());
  });
};

function pendingOf(mod: HTMLElement): Set<string> {
  let set = pending.get(mod);
  if (!set) {
    set = new Set();
    pending.set(mod, set);
  }
  return set;
}

export function progressStarted(mod: HTMLElement, key: string): void {
  pendingOf(mod).add(key);
  progress.set(mod, { ...(progress.get(mod) ?? EMPTY), current: key });
  emit();
}

export function progressFinished(mod: HTMLElement, key: string): void {
  const set = pendingOf(mod);
  set.delete(key);
  const was = progress.get(mod) ?? EMPTY;
  progress.set(mod, { done: was.done + 1, current: [...set].pop() ?? null });
  emit();
}

/** Retry starts the count again. */
export function progressReset(mod: HTMLElement): void {
  progress.delete(mod);
  pending.delete(mod);
  emit();
}

export function useLoadProgress(mod: HTMLElement | null): LoadProgress {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => (mod ? (progress.get(mod) ?? EMPTY) : EMPTY),
  );
}

// ── The page-wide bar ────────────────────────────────────────────────────────
// "Loading 16 modules… 9 ready" across the top while 2+ modules are loading (e.g. after "All").
// It only counts loading screens as they come and go — it asks for nothing itself.

const loadingMods = new Set<HTMLElement>();
let burstTotal = 0;
let pageBar: HTMLElement | null = null;
let hideTimer: number | undefined;

function renderPageBar(): void {
  const loading = loadingMods.size;
  burstTotal = Math.max(burstTotal, loading);
  if (!pageBar) {
    pageBar = document.createElement("div");
    pageBar.className = "live-page-progress";
    pageBar.innerHTML = '<div class="live-page-progress-fill"></div><div class="live-page-progress-text"></div>';
    document.body.appendChild(pageBar);
  }
  const ready = burstTotal - loading;
  const show = burstTotal >= 2;
  (pageBar.firstElementChild as HTMLElement).style.width = `${burstTotal ? Math.round((ready / burstTotal) * 100) : 0}%`;
  (pageBar.lastElementChild as HTMLElement).textContent =
    loading > 0 ? `Loading ${burstTotal} modules… ${ready} ready` : `All ${burstTotal} modules ready`;
  window.clearTimeout(hideTimer);
  pageBar.classList.toggle("visible", show);
  if (loading === 0) {
    // Linger a moment on "ready", then hide; the next burst counts from zero.
    hideTimer = window.setTimeout(() => {
      pageBar?.classList.remove("visible");
      burstTotal = 0;
    }, 900);
  }
}

/** A module's loading screen appeared / went away. */
export function moduleLoading(mod: HTMLElement, loading: boolean): void {
  if (loading) loadingMods.add(mod);
  else loadingMods.delete(mod);
  renderPageBar();
}

/** What a call is doing, in plain words, from its query key (hooks/*, modules/*). */
export function stepLabel(key: string | null, player: string | null): string {
  const who = player ? `${player}'s` : "the player's";
  if (key === null) return "Getting ready";
  const [a = "", b = ""] = key.split(":");
  if (b === "comps") return `Finding ${who} competitions`;
  if (b === "seasons" && a !== "files") return `Finding ${who} seasons`;
  if (b === "season" && a !== "matches") return `Loading ${who} events`;
  if (b === "sticky" || (a === "group" && b === "league")) return `Finding ${who} club and league`;
  switch (a) {
    case "stats":
      return b === "meta" ? `Finding ${who} seasons` : `Loading ${who} numbers`;
    case "files":
      return b === "seasons" ? "Checking which seasons are ready" : "Downloading the comparison group";
    case "group":
      return "Downloading the comparison group";
    case "track":
      return "Loading the scale";
    case "movers":
      return "Checking league moves";
    case "details":
      return `Loading ${who} details`;
    case "league":
      return b === "choices" ? "Loading the leagues" : "Loading the table";
    case "squad":
      return b === "teams" ? "Loading the clubs" : "Loading the squad";
    case "matches":
      return `Loading ${who} matches`;
    default:
      return "Loading the data";
  }
}

/** Roughly how many calls each module makes the first time (from its hooks) — so the bar
 *  can say "2 of 3". A guess, so it never claims 100%: it grows if a module asks for more. */
const EXPECTED_STEPS: Record<string, number> = {
  Appearance: 8,
  Passing: 8,
  Attacking: 8,
  Defending: 8,
  Goalkeeping: 8,
  "Bio": 1,
  Match: 1,
  "League Table": 3,
  "Match Results": 3,
  Squad: 4,
  Matches: 2,
  "Shot Map": 3,
  "Pass Map": 3,
  "Heat Map": 3,
  "Passing Profile": 3,
  "Passing Report": 3,
  Radar: 3,
  Ranking: 3,
  Filter: 3,
  "Comparison": 3,
};

export function expectedSteps(title: string, done: number): number {
  return Math.max(EXPECTED_STEPS[title] ?? 3, done + 1);
}
